import { useEffect, useMemo, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  fetchTaxGroupById,
  getTaxComponentsDdl,
  handleSubmitTaxGroup,
  ITaxComponentsValue,
  ITaxGroupForm,
  TAX_GROUPE_INITIAL_VALUE,
  taxGroupValidationSchema,
} from "./TaxGroupFormController";
import {
  hasAnyModification,
  prepareMessageFromParams,
  sweetAlertError,
} from "../../utils/AppFunctions";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import { Formik, Form } from "formik";
import { ICFormikTextInput } from "../../core-component/formik";
import { ICCheckbox } from "../../core-component/form";
import { ICStateOState, ITaxGroups } from "./TaxGroupsViewController";
import { STATE_TYPE } from "../../utils/AppEnumerations";
import { cn } from "../../lib";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";

interface ITaxGroupFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  taxGroupToEdit?: ITaxGroups;
}

const TaxGroupForm: React.FC<ITaxGroupFormProps> = ({
  handleClose,
  readOnly,
  taxGroupToEdit,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [taxGroupFormInitialValues, setTaxGroupFormInitialValues] =
    useState<ITaxGroupForm>();
  const [taxGroupsCompanyState, setTaxGroupsCompanyState] = useState<
    ITaxComponentsValue[]
  >([]);
  const [taxGroupsOtherState, setTaxGroupsOtherState] = useState<
    ITaxComponentsValue[]
  >([]);
  const [activeTab, setActiveTab] = useState<STATE_TYPE>(STATE_TYPE.Company);
  const [selectCompanyState, setSelectCompanyState] = useState<string[]>([]);
  const [selectOtherState, setSelectOtherState] = useState<string[]>([]);
  const [showTotalMismatchWarning, setShowTotalMismatchWarning] =
    useState(false);
  const [showNotSelectWarning, setShowNotSelectWarning] = useState(false);
  const [totalMismatch, setTotalMismatch] = useState<ITaxGroupForm | null>(
    null,
  );
  const [notSelectValue, setNotSelectValue] = useState<ITaxGroupForm | null>(
    null,
  );

  useEffect(() => {
    if (taxGroupToEdit) {
      getTaxGroupFieldById(taxGroupToEdit.id);
    } else {
      setTaxGroupFormInitialValues(TAX_GROUPE_INITIAL_VALUE);
    }
    fetchTaxComponents();
  }, []);

  useEffect(() => {
    if (taxGroupFormInitialValues?.tax_component) {
      const companyStateIds = taxGroupFormInitialValues.tax_component
        .filter((item: ICStateOState) => item.type === STATE_TYPE.CompanyState)
        .map((item: ICStateOState) => item.id);
      const otherStateIds = taxGroupFormInitialValues.tax_component
        .filter((item: ICStateOState) => item.type === STATE_TYPE.OtherState)
        .map((item: ICStateOState) => item.id);

      setSelectCompanyState(companyStateIds);
      setSelectOtherState(otherStateIds);
    }
  }, [taxGroupFormInitialValues]);

  const toggleItemSelection = (itemDataValue: string) => {
    setSelectCompanyState((prevState) =>
      prevState.includes(itemDataValue)
        ? prevState.filter((val) => val !== itemDataValue)
        : [...prevState, itemDataValue],
    );
  };
  const toggleOtherItemSelection = (itemDataValue: string) => {
    setSelectOtherState((prevState) =>
      prevState.includes(itemDataValue)
        ? prevState.filter((val) => val !== itemDataValue)
        : [...prevState, itemDataValue],
    );
  };
  const getTaxGroupFieldById = async (id: string) => {
    setLoading(true);
    await fetchTaxGroupById(id, handleClose, setTaxGroupFormInitialValues);
    setLoading(false);
  };

  const fetchTaxComponents = async () => {
    setLoading(true);
    await getTaxComponentsDdl(
      selectCompanyState,
      setTaxGroupsCompanyState,
      selectOtherState,
      setTaxGroupsOtherState,
      setLoading,
    );
  };

  const checkFormModification = (values: ITaxGroupForm) => {
    if (
      taxGroupFormInitialValues &&
      hasAnyModification(values, taxGroupFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const companyStateTotal = useMemo(
    () =>
      taxGroupsCompanyState
        .filter((item) => selectCompanyState.includes(item.data_value))
        .reduce((company, item) => company + Number(item.rate), 0)
        .toFixed(2),
    [selectCompanyState, taxGroupsCompanyState],
  );

  const otherStateTotal = useMemo(
    () =>
      taxGroupsOtherState
        .filter((item) => selectOtherState.includes(item.data_value))
        .reduce((other, item) => other + Number(item.rate), 0)
        .toFixed(2),
    [selectOtherState, taxGroupsOtherState],
  );
  const handleSubmit = async (values: ITaxGroupForm) => {
    try {
      if (selectCompanyState.length === 0 && selectOtherState.length === 0) {
        sweetAlertError({
          message: t("state_error_message_required"),
        });
        return;
      } else if (
        selectOtherState.length === 0 ||
        selectCompanyState.length === 0
      ) {
        setShowNotSelectWarning(true);
        setNotSelectValue(values);
      } else if (companyStateTotal !== otherStateTotal) {
        setShowTotalMismatchWarning(true);
        setTotalMismatch(values);
      } else {
        handleSubmitTaxGroup(
          {
            ...values,
            company_state: selectCompanyState,
            other_state: selectOtherState,
          },
          setSubmitLoading,
          taxGroupToEdit?.id,
          handleClose,
        );
      }
    } catch {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        taxGroupFormInitialValues && (
          <Formik
            initialValues={taxGroupFormInitialValues}
            validationSchema={taxGroupValidationSchema(t)}
            onSubmit={(values) => {
              handleSubmit(values);
            }}
          >
            {(formikProps) => {
              const { handleSubmit, values } = formikProps;

              return (
                <FormRightModalWrapper
                  containerClassName="!w-1/4"
                  onClose={() => checkFormModification(values)}
                  title={t("tax_group")}
                  footer={{
                    created_date: taxGroupFormInitialValues.created_date,
                    modified_date: taxGroupFormInitialValues.modified_date,
                    created_by: taxGroupFormInitialValues.created_by,
                    modified_by: taxGroupFormInitialValues.modified_by,
                  }}
                >
                  <Form
                    onSubmit={handleSubmit}
                    className="flex flex-col flex-1 overflow-hidden "
                  >
                    <div className="flex-1 flex flex-col pt-2.5 overflow-y-scroll">
                      <ICFormikTextInput
                        {...formikProps}
                        fieldName={"name"}
                        placeholder={t("name")}
                        required
                        tabIndex={0}
                        disabled={readOnly}
                      />
                      <div className="w-full flex flex-col flex-1 overflow-hidden my-4">
                        <div className="relative right-0">
                          <div className="relative flex flex-wrap px-1.5 pt-1.5 list-none rounded-t-lg bg-base">
                            <div className="z-30 flex-auto text-center">
                              <div
                                className={cn(
                                  "z-30 flex items-center justify-center w-full px-0 py-2 text-sm transition-all ease-in-out border-0 rounded-t-lg cursor-pointer text-muted",
                                  {
                                    "text-default bg-subtle":
                                      activeTab === STATE_TYPE.Company,
                                    "text-muted hover:text-default":
                                      activeTab !== STATE_TYPE.Company,
                                  },
                                )}
                                onClick={() => setActiveTab(STATE_TYPE.Company)}
                              >
                                {t("company_state")}
                              </div>
                            </div>
                            <div className="z-30 flex-auto text-center">
                              <div
                                className={cn(
                                  "z-30 flex items-center justify-center w-full px-0 py-2 text-sm transition-all ease-in-out border-0 rounded-t-lg cursor-pointer text-muted",
                                  {
                                    "text-default bg-subtle":
                                      activeTab === STATE_TYPE.Other,
                                    "text-muted hover:text-default":
                                      activeTab !== STATE_TYPE.Other,
                                  },
                                )}
                                onClick={() => setActiveTab(STATE_TYPE.Other)}
                              >
                                {t("other_state")}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-b-lg shadow-md pb-1.5 px-1.5 bg-base flex flex-col flex-1 overflow-hidden">
                          <div className="pt-4 bg-subtle overflow-y-scroll flex-1">
                            {activeTab === STATE_TYPE.Company &&
                              taxGroupsCompanyState.length > 0 &&
                              taxGroupsCompanyState.map((item) => (
                                <div
                                  key={item.data_value}
                                  className="flex justify-between items-center p-2 rounded-lg"
                                >
                                  <span className="text-sm font-medium text-default">
                                    {item.display_value}
                                  </span>
                                  <div className="flex items-center">
                                    <div className="text-sm font-medium text-success">
                                      {item.rate}%
                                    </div>
                                    <div className="ml-2">
                                      <ICCheckbox
                                        checkboxClassName="size-3.5"
                                        checked={selectCompanyState.includes(
                                          item.data_value,
                                        )}
                                        onChange={() =>
                                          toggleItemSelection(item.data_value)
                                        }
                                        disabled={readOnly}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            {activeTab === STATE_TYPE.Other &&
                              taxGroupsOtherState.length > 0 &&
                              taxGroupsOtherState.map((item) => (
                                <div
                                  key={item.data_value}
                                  className="flex justify-between items-center p-2 rounded-lg"
                                >
                                  <span className="text-sm font-medium text-default">
                                    {item.display_value}
                                  </span>
                                  <div className="flex items-center">
                                    <div className="text-sm font-medium text-success">
                                      {item.rate}%
                                    </div>
                                    <div className="ml-2">
                                      <ICCheckbox
                                        checkboxClassName="size-3.5"
                                        checked={selectOtherState.includes(
                                          item.data_value,
                                        )}
                                        onChange={() =>
                                          toggleOtherItemSelection(
                                            item.data_value,
                                          )
                                        }
                                        disabled={readOnly}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {!readOnly && (
                      <ICButton
                        loading={submitLoading}
                        className={"w-full"}
                        children={t("submit")}
                      />
                    )}
                  </Form>
                </FormRightModalWrapper>
              );
            }}
          </Formik>
        )
      )}
      {showNotSelectWarning && (
        <ICConfirmationModal
          title={t("unselected_component_rate")}
          message={prepareMessageFromParams(
            t("unselected_component_rate_massage"),
            [
              [
                "state",
                selectCompanyState.length === 0
                  ? "Company state"
                  : "Other state",
              ],
            ],
          )}
          onClose={(value) => {
            setShowNotSelectWarning(false);
            if (value && notSelectValue) {
              handleSubmitTaxGroup(
                {
                  ...notSelectValue,
                  company_state: selectCompanyState,
                  other_state: selectOtherState,
                },
                setSubmitLoading,
                taxGroupToEdit?.id,
                handleClose,
              );
            }
          }}
        />
      )}
      {showTotalMismatchWarning && (
        <ICConfirmationModal
          title={t("total_mismatch_warning_title")}
          message={prepareMessageFromParams(
            t("totals_do_not_match_confirm_submit"),
            [
              ["companyTotal", companyStateTotal],
              ["otherTotal", otherStateTotal],
            ],
          )}
          onClose={(value) => {
            setShowTotalMismatchWarning(false);
            if (value && totalMismatch) {
              handleSubmitTaxGroup(
                {
                  ...totalMismatch,
                  company_state: selectCompanyState,
                  other_state: selectOtherState,
                },
                setSubmitLoading,
                taxGroupToEdit?.id,
                handleClose,
              );
            }
          }}
        />
      )}
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(value) => {
            setShowDiscardWarning(false);
            value && handleClose(false);
          }}
        />
      )}
    </>
  );
};

export default TaxGroupForm;
